/**
 * Roadmap sorting — the grid's column comparator and the kanban's within-column
 * comparator, kept together so both agree on rank order.
 */
import { priorityRank, scopeRank, statusRank } from './roadmapMeta';
import { toSortMillis } from '../../utils/roadmapDates';
import type { RoadmapItem } from '../../types';

export type RoadmapSortKey =
  | 'title'
  | 'status'
  | 'priority'
  | 'owner'
  | 'estimatedScope'
  | 'updatedAt';

export type RoadmapSortDir = 'asc' | 'desc';

/** Grid column sort. `dir` is applied by the caller's multiplier. */
export function compareByKey(
  a: RoadmapItem,
  b: RoadmapItem,
  key: RoadmapSortKey,
): number {
  switch (key) {
    case 'title':
      return (a.title ?? '').localeCompare(b.title ?? '');
    case 'status':
      return statusRank(a.status) - statusRank(b.status);
    case 'priority':
      return priorityRank(a.priority) - priorityRank(b.priority);
    case 'owner':
      return (a.owner ?? '').localeCompare(b.owner ?? '');
    case 'estimatedScope':
      return scopeRank(a.estimatedScope) - scopeRank(b.estimatedScope);
    case 'updatedAt':
    default:
      return toSortMillis(a.updatedAt) - toSortMillis(b.updatedAt);
  }
}

export function sortRows(
  rows: RoadmapItem[],
  key: RoadmapSortKey,
  dir: RoadmapSortDir,
): RoadmapItem[] {
  const multiplier = dir === 'asc' ? 1 : -1;
  // Copy first — Array.prototype.sort mutates, and callers pass memoised arrays.
  return [...rows].sort((a, b) => compareByKey(a, b, key) * multiplier);
}

/**
 * Kanban card order within a column: P0 top → P3 bottom, then most recently
 * updated first inside a priority band.
 */
export function sortKanbanColumn(rows: RoadmapItem[]): RoadmapItem[] {
  return [...rows].sort((a, b) => {
    const byPriority = priorityRank(a.priority) - priorityRank(b.priority);
    if (byPriority !== 0) return byPriority;
    return toSortMillis(b.updatedAt) - toSortMillis(a.updatedAt);
  });
}
